import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
// WalletConnect is loaded dynamically to avoid crypto polyfill issues at import time
import {
  createPublicClient,
  createWalletClient,
  custom,
  formatEther,
  http,
  type Address,
  type Chain,
  type PublicClient,
  type WalletClient,
} from 'viem';
import { bsc, bscTestnet, sepolia } from 'viem/chains';
import { CHAIN_ADD_CONFIGS, RDNS_TO_WALLET_TYPE, WALLET_STORAGE_KEY } from '../constants/chains';
import { CUSTOS_ENVIRONMENT, CustosEnvironment } from '../models/environment';
import { EIP1193Provider, EIP6963ProviderDetail, WalletType } from '../models/wallet.types';
import { resolveLegacyProvider, isValidAddress } from '../utils/eip1193';
import { safeGetItem, safeRemoveItem, safeSetItem } from '../utils/storage';

@Injectable({ providedIn: 'root' })
export class WalletService {
  // ─── Public Signals ──────────────────────────────────────────
  private readonly _address = signal<Address | null>(null);
  readonly address = this._address.asReadonly();

  private readonly _chainId = signal<number | null>(null);
  readonly chainId = this._chainId.asReadonly();

  readonly isConnected = computed(() => this._address() !== null);

  readonly shortAddress = computed(() => {
    const addr = this._address();
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  });

  readonly isCorrectChain = computed(() => this._chainId() === this.env.chainId);

  private readonly _walletType = signal<WalletType | null>(null);
  readonly walletType = this._walletType.asReadonly();

  private readonly _isConnecting = signal(false);
  readonly isConnecting = this._isConnecting.asReadonly();

  private readonly _balance = signal<bigint | null>(null);
  readonly balance = this._balance.asReadonly();

  readonly balanceFormatted = computed(() => {
    const b = this._balance();
    return b !== null ? formatEther(b) : '0';
  });

  private readonly _balanceVersion = signal(0);
  readonly balanceVersion = this._balanceVersion.asReadonly();

  /** EIP-6963: Discovered wallet providers */
  readonly discoveredProviders = signal<EIP6963ProviderDetail[]>([]);

  // ─── Private State ───────────────────────────────────────────
  private rawProvider: EIP1193Provider | null = null;
  private wcProvider: InstanceType<typeof import('@walletconnect/ethereum-provider').EthereumProvider> | null = null;
  private publicClient: PublicClient | null = null;
  private walletClient: WalletClient | null = null;
  private readonly eip6963Providers = new Map<string, EIP6963ProviderDetail>();
  private readonly zone = inject(NgZone);
  private readonly env = inject<CustosEnvironment>(CUSTOS_ENVIRONMENT);

  private get chain(): Chain {
    if (this.env.chainId === 56) return bsc;
    if (this.env.chainId === 11155111) return sepolia;
    return bscTestnet;
  }

  /** Read-only client using env RPC — independent of wallet chain */
  getPublicClient(): PublicClient {
    if (!this.publicClient) {
      this.publicClient = createPublicClient({
        chain: this.chain,
        transport: http(this.env.rpcUrl),
      });
    }
    return this.publicClient;
  }

  /** Wallet client for write operations — requires connected wallet */
  getWalletClient(): WalletClient {
    if (!this.walletClient) throw new Error('Wallet not connected');
    return this.walletClient;
  }

  // ─── EIP-1193 Event Handlers ─────────────────────────────────
  private onAccountsChanged = (accounts: unknown) => {
    const accts = accounts as string[];
    this.zone.run(() => {
      if (accts.length === 0) {
        this.disconnect();
      } else if (this.isValidAddress(accts[0])) {
        this._address.set(accts[0] as Address);
        this.refreshBalance();
      }
    });
  };

  private onChainChanged = (chainIdHex: unknown) => {
    this.zone.run(() => {
      this._chainId.set(Number(chainIdHex as string));
      this.recreateWalletClient();
      this.refreshBalance();
    });
  };

  private onDisconnect = () => {
    this.zone.run(() => this.disconnect());
  };

  constructor() {
    if (this.env.useMockApi) {
      // In mock mode, set a fake wallet address so stores don't early-return
      this._address.set('0xaf5cb9e868e0c1664c6e480a9907a4ea0c92a833' as Address);
      this._chainId.set(this.env.chainId);
      return;
    }
    this.initEIP6963();
    this.tryReconnect();
  }

  // ─── Public API ──────────────────────────────────────────────

  async connect(type: WalletType): Promise<void> {
    this._isConnecting.set(true);
    try {
      if (type === 'walletconnect') {
        await this.connectWalletConnect();
      } else {
        await this.connectBrowser(type);
      }
      this._walletType.set(type);
      safeSetItem(WALLET_STORAGE_KEY, type);
    } catch (err) {
      console.error('[WalletService] connect error:', err);
      throw err;
    } finally {
      this._isConnecting.set(false);
    }
  }

  disconnect(): void {
    this._address.set(null);
    this._chainId.set(null);
    this._walletType.set(null);
    this._isConnecting.set(false);
    this._balance.set(null);
    this.walletClient = null;
    safeRemoveItem(WALLET_STORAGE_KEY);
    this.removeListeners();
    if (this.wcProvider) {
      void this.wcProvider.disconnect();
      this.wcProvider = null;
    }
    this.rawProvider = null;
  }

  /** EIP-3085: Switch to the target chain, adding it if needed */
  async switchChain(): Promise<void> {
    if (!this.rawProvider) throw new Error('Wallet not connected');
    const hexChainId = '0x' + this.env.chainId.toString(16);
    try {
      await this.rawProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    } catch (err: unknown) {
      const rpcErr = err as { code?: number };
      if (rpcErr?.code === 4902) {
        await this.addChain(hexChainId);
      } else {
        throw err;
      }
    }
    // Read the actual chain ID from the wallet after the operation
    const currentChainHex = await this.rawProvider!.request({ method: 'eth_chainId' }) as string;
    this._chainId.set(Number(currentChainHex));
    this.recreateWalletClient();
  }

  isValidAddress(address: string): boolean {
    return isValidAddress(address);
  }

  async refreshBalance(): Promise<void> {
    const addr = this.address();
    if (addr) {
      try {
        const bal = await this.getPublicClient().getBalance({ address: addr });
        if (this.address() === addr) {
          this._balance.set(bal);
        }
      } catch (err) {
        console.warn('[WalletService] refreshBalance failed:', err);
      }
    }
    this._balanceVersion.update((v) => v + 1);
  }

  bumpBalanceVersion(): void {
    this._balanceVersion.update((v) => v + 1);
  }

  // ─── Private: Connection ─────────────────────────────────────

  private async connectBrowser(type: WalletType): Promise<void> {
    const provider = this.resolveProvider(type);
    if (!provider) throw new Error('No browser wallet detected');
    this.rawProvider = provider;

    try {
      await provider.request({ method: 'eth_requestAccounts' });
    } catch (err: unknown) {
      const rpcErr = err as { code?: number; error?: { code?: number } };
      if (rpcErr?.code === 4001 || rpcErr?.error?.code === 4001) {
        throw new Error('User rejected connection');
      }
      if (rpcErr?.code === -32002 || rpcErr?.error?.code === -32002) {
        const accounts = await this.pollAccounts();
        if (accounts.length === 0) throw new Error('User rejected connection');
        this._address.set(accounts[0] as Address);
        const chainIdHex = (await provider.request({ method: 'eth_chainId' })) as string;
        this._chainId.set(Number(chainIdHex));
        this.createWalletClient(provider);
        await this.refreshBalance();
        this.listenEvents();
        return;
      }
      throw err;
    }
    const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
    this._address.set(accounts[0] as Address);
    const chainIdHex = (await provider.request({ method: 'eth_chainId' })) as string;
    this._chainId.set(Number(chainIdHex));
    this.createWalletClient(provider);
    await this.refreshBalance();
    this.listenEvents();
  }

  private async connectWalletConnect(): Promise<void> {
    // WalletConnect requires @walletconnect/ethereum-provider which needs Node crypto polyfill.
    // Defer until a WalletConnect project ID is configured and polyfill is set up.
    if (!this.env.walletConnectProjectId) {
      throw new Error('WalletConnect project ID not configured');
    }
    // Dynamic import — only loaded when actually needed
    const { EthereumProvider } = await import(
      /* webpackChunkName: "walletconnect" */
      '@walletconnect/ethereum-provider'
    );
    this.wcProvider = await EthereumProvider.init({
      projectId: this.env.walletConnectProjectId,
      chains: [this.env.chainId],
      showQrModal: true,
    });
    await this.wcProvider.enable();
    this.rawProvider = this.wcProvider as unknown as EIP1193Provider;
    const accounts = (await this.wcProvider.request({ method: 'eth_accounts' })) as string[];
    this._address.set(accounts[0] as Address);
    const chainIdHex = (await this.wcProvider.request({ method: 'eth_chainId' })) as string;
    this._chainId.set(Number(chainIdHex));
    this.createWalletClient(this.rawProvider);
    await this.refreshBalance();
    this.listenEvents();
  }

  private async tryReconnect(): Promise<void> {
    const savedType = safeGetItem(WALLET_STORAGE_KEY) as WalletType | null;
    if (!savedType) return;
    try {
      if (savedType === 'walletconnect') {
        // WalletConnect sessions don't persist across page reloads without session storage.
        // Don't re-trigger QR modal — just clear the saved type.
        safeRemoveItem(WALLET_STORAGE_KEY);
        return;
      } else {
        const provider = this.resolveProvider(savedType);
        if (!provider) return;
        this.rawProvider = provider;
        const accounts = (await provider.request({ method: 'eth_accounts' })) as string[];
        if (accounts.length === 0 || !this.isValidAddress(accounts[0])) {
          safeRemoveItem(WALLET_STORAGE_KEY);
          return;
        }
        this._address.set(accounts[0] as Address);
        const chainIdHex = (await provider.request({ method: 'eth_chainId' })) as string;
        this._chainId.set(Number(chainIdHex));
        this._walletType.set(savedType);
        this.createWalletClient(provider);
        this.listenEvents();
        await this.refreshBalance();
      }
    } catch {
      safeRemoveItem(WALLET_STORAGE_KEY);
    }
  }

  // ─── Private: Viem Clients ───────────────────────────────────

  private createWalletClient(provider: EIP1193Provider): void {
    this.walletClient = createWalletClient({
      chain: this.chain,
      transport: custom(provider as Parameters<typeof custom>[0]),
    });
  }

  private recreateWalletClient(): void {
    if (this.rawProvider) {
      this.createWalletClient(this.rawProvider);
    }
  }

  // ─── Private: EIP-3085 Chain Add ─────────────────────────────

  private async addChain(hexChainId: string): Promise<void> {
    const config = CHAIN_ADD_CONFIGS[this.env.chainId];
    if (!config) {
      throw new Error(`No chain configuration for chainId ${this.env.chainId}`);
    }
    try {
      await this.rawProvider!.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: hexChainId,
            chainName: config.chainName,
            nativeCurrency: config.nativeCurrency,
            rpcUrls: config.rpcUrls,
            blockExplorerUrls: config.blockExplorerUrls,
          },
        ],
      });
    } catch (addErr: unknown) {
      const rpcErr = addErr as { code?: number };
      if (rpcErr?.code === 4001) {
        throw new Error('User rejected adding the network');
      }
      throw new Error('Failed to add network — please add it manually in your wallet settings');
    }
    // EIP-3085: auto-switch not guaranteed, explicitly switch
    const currentChainHex = (await this.rawProvider!.request({ method: 'eth_chainId' })) as string;
    if (Number(currentChainHex) !== this.env.chainId) {
      await this.rawProvider!.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    }
  }

  // ─── Private: EIP-6963 Discovery ─────────────────────────────

  private initEIP6963(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener(
      'eip6963:announceProvider' as string,
      ((event: CustomEvent<EIP6963ProviderDetail>) => {
        const detail = event.detail;
        if (!detail?.info?.rdns || !detail?.provider) return;
        this.zone.run(() => {
          this.eip6963Providers.set(detail.info.rdns, detail);
          this.discoveredProviders.set([...this.eip6963Providers.values()]);
        });
      }) as EventListener,
    );
    window.dispatchEvent(new Event('eip6963:requestProvider'));
  }

  resolveProvider(type: WalletType): EIP1193Provider | undefined {
    // Prefer EIP-6963 announcement for the requested wallet — the modern,
    // multi-provider-safe path. Fall back to a type-specific legacy lookup
    // (window.metaMask via isMetaMask flag, window.trustwallet, window.okxwallet)
    // when the wallet hasn't shipped EIP-6963 yet. Critically: NEVER return
    // a provider whose injection slot doesn't match the chosen type — the
    // old greedy fallback handed back OKX whenever it was installed, even
    // when the user picked MetaMask (Wesley 2026-05-21).
    for (const [rdns, walletType] of Object.entries(RDNS_TO_WALLET_TYPE)) {
      if (walletType === type) {
        const detail = this.eip6963Providers.get(rdns);
        if (detail) return detail.provider;
      }
    }
    return resolveLegacyProvider(type);
  }

  // ─── Private: EIP-1193 Events ────────────────────────────────

  private listenEvents(): void {
    this.removeListeners(); // Prevent duplicate listeners
    const target = this.wcProvider ?? this.rawProvider;
    if (!target) return;
    target.on('accountsChanged', this.onAccountsChanged);
    target.on('chainChanged', this.onChainChanged);
    target.on('disconnect', this.onDisconnect);
  }

  private removeListeners(): void {
    const target = this.wcProvider ?? this.rawProvider;
    if (!target) return;
    target.removeListener('accountsChanged', this.onAccountsChanged);
    target.removeListener('chainChanged', this.onChainChanged);
    target.removeListener('disconnect', this.onDisconnect);
  }

  // ─── Private: Polling ────────────────────────────────────────

  private pollAccounts(): Promise<string[]> {
    return new Promise((resolve) => {
      let resolved = false;
      const interval = setInterval(async () => {
        if (!this.rawProvider || resolved) {
          clearInterval(interval);
          if (!resolved) {
            resolved = true;
            resolve([]);
          }
          return;
        }
        try {
          const accounts = (await this.rawProvider.request({ method: 'eth_accounts' })) as string[];
          if (accounts.length > 0) {
            clearInterval(interval);
            resolved = true;
            resolve(accounts);
          }
        } catch {
          /* retry */
        }
      }, 500);
      setTimeout(() => {
        clearInterval(interval);
        if (!resolved) {
          resolved = true;
          resolve([]);
        }
      }, 120_000);
    });
  }
}
